import {Chart as ChartJS,CategoryScale,LinearScale,PointElement,LineElement,Title,Tooltip,Filler,Legend,LineController,BarController,BarElement} from 'chart.js';
import { Chart as ReactChart } from 'react-chartjs-2';
import { DatagridHelper } from '../../datagrid/DatagridHelper';
import { DatagridColumn } from '../../utils/types';
import dayjs from "../../utils/dayjs";

ChartJS.register(CategoryScale,LinearScale,PointElement,LineElement,Title,Tooltip,Filler,Legend,LineController,BarElement,BarController);

export default function Chart(props: any) {
  const isMultiChart = props.yAxis.length > 1 ? true : false;
  const yAxisLength = props.yAxis.length;
  const datasets:any[] = [];

  const options:any = {
    responsive: true,
    aspectRatio: 4,
    tooltips: {
      mode : 'index',
    },
    plugins: {
      legend: {
        position: 'top' as const,
        onClick: (click:any, legendItem:any, legend:any) => {
          // show/hide dataset
          const datasetTexts = legend.legendItems.map((dataset:any, index:number) => dataset.text);
          const index:number = datasetTexts.indexOf(legendItem.text);
          if (legend.chart.isDatasetVisible(index)) {
            legend.chart.hide(index);
          } else {
            legend.chart.show(index);
          }
          
          // show/hide y axis checking shared axies
          const newOptions = Object.assign({}, options);
          const scalesValues:any[] = Object.values(newOptions.scales);
          const scalesKeys:any[] = Object.keys(newOptions.scales);
          Object.keys(scalesValues).forEach((index:any) => {
            if (index === 0) return;
            if (scalesValues[index].title && scalesValues[index].title.datasetNames.includes(legendItem.text)) {

              if (!scalesValues[index].display) {
                scalesValues[index].display = true;
              } else {
                let cantHideYAxis = true;
                const yAxisToSearch = scalesKeys[index];
                datasets.forEach((dataset, datasetIndex) => {
                  if (dataset.yAxisID === yAxisToSearch && legend.chart.isDatasetVisible(datasetIndex)) {
                    cantHideYAxis = false;
                  }
                });
                if (cantHideYAxis) {
                  scalesValues[index].display = false;
                }
              }
            }
          });

          // Hide all grids
          Object.keys(scalesValues).forEach((index:any) => {
            if (index === 0) return;
            if (scalesValues[index].grid) {
              scalesValues[index].grid.display = false;
            }
          });
          // show the grid of first y axis visible
          let gridShowed = false;
          Object.keys(scalesValues).forEach((index:any) => {
            if (index === 0) return;
            if (!gridShowed && scalesValues[index].display && scalesValues[index].grid && !scalesValues[index].grid.display) {
              scalesValues[index].grid.display = true;
              gridShowed = true;
            }
          });

          // update chart
          legend.chart.options = newOptions;
          legend.chart.update();
        },
        labels: {
          usePointStyle: true,
          generateLabels: (chart:any) => {
            return chart.data.datasets.map(
              (dataset:any, index:number) => ({
                text: dataset.label,
                fillStyle: dataset.backgroundColor,
                strokeStyle: dataset.borderColor,
                pointStyle: dataset.type === "line" && !dataset.showLine ? "circle" : (dataset.type === "bar" ? "rect" : "line"),
                hidden: !chart.isDatasetVisible(index)
              })
            );
          }
        }
      },
      tooltip: {
        animation: {
          duration: 0
        },
        interaction: {
          mode: 'index',
          axis: 'x',
          intersect: true
        },
        callbacks: {
          label: (context:any) => ` ${props.columns[context.datasetIndex].name}: ${DatagridHelper.formatValue(props.columns[context.datasetIndex].type, context.raw, props.columns[context.datasetIndex].precision)}`
        }
      }
    },
    scaleShowValues: true,
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          autoSkipPadding: 20
        },
        grid: {
          display: false
        }
      }
    }
  };

  // build labels and datasets
  const labels = props.xAxis
                  .filter((x:any) => dayjs.utc(x.id, "YYYY-MM-DD").isValid() || dayjs.utc(x.id, "YYYY-MM").isValid() || x.id.match("^[0-9]{4}-W[0-9]{2}") )
                  .map((x:any) => x.name);

  props.yAxis.forEach((row:any, datasetIndex:number) => {
    const values:Number[] = [];
    labels.forEach((label:string) => {
      values.push(row[label]);
    });

    let backgroundColor = "rgba(53, 162, 235, 0.5)";
    let borderColor = "rgb(53, 162, 235)";
    if (isMultiChart) {
      switch(datasetIndex) {
        case 0:
          if (yAxisLength === 3) {
            backgroundColor = "#337CF6";
            borderColor = "#337CF6";
          } else {
            backgroundColor = "rgba(85, 85, 85, 1)";
            borderColor = "rgb(85, 85, 85)";
          }
          break;
        case 1:
          if (yAxisLength === 3) {
            backgroundColor = "rgba(85, 85, 85, 1)";
            borderColor = "rgb(85, 85, 85)";
          } else {
            backgroundColor = "rgba(129, 199, 132, 0.5)";
            borderColor = "rgb(129, 199, 132)";
          }
          break;
        case 2:
          backgroundColor = "rgba(129, 199, 132, 0.5)";
          borderColor = "rgb(129, 199, 132)";
          break;
      }
    }

    let datasetType;
    switch(yAxisLength) {
      case 1:
        datasetType = "line" as const;
        break;
      case 2:
        if (datasetIndex === 0) {
          datasetType = "line" as const;
        } else {
          datasetType = "bar" as const;
        }
        break;
      case 3:
        if (datasetIndex === 2) {
          datasetType = "bar" as const;
        } else {
          datasetType = "line" as const;
        }
        break;
    }

    const dataset = {
      type: datasetType,
      fill: isMultiChart ? false : true,
      label: props.columns[datasetIndex].name,
      data: values.reverse(),
      borderColor: borderColor,
      borderWidth: 2,
      backgroundColor: backgroundColor,
      showLine: yAxisLength > 2 && datasetIndex === 0 ? false : true,
      pointRadius: yAxisLength > 2 && datasetIndex === 1 ? 0 : 5,
      yAxisID: props.columns[datasetIndex].axisId ? props.columns[datasetIndex].axisId : props.columns[datasetIndex].id
    };
    datasets.push(dataset);
  });

  // build y axies
  let index:number = 0;
  props.columns.forEach((column:DatagridColumn) => {
    const axisId = column.axisId ? column.axisId : column.id;

    if (!options.scales[axisId as keyof DatagridColumn]) {
      const datasetNames:any[] = [];
      let text:string = "";

      // build text of y axis
      props.columns.forEach((columnForReadText:DatagridColumn) => {
        if (column.axisId !== columnForReadText.axisId) return;
        if (text) text += " | ";
        text += columnForReadText.name;
        datasetNames.push(columnForReadText.name);
      });

      options.scales[axisId as keyof DatagridColumn] = {
        display: true,
        title: {
          display: true,
          text: text,
          datasetNames: datasetNames
        },
        grid: {
          display: index === 0 ? true : false,
        },
        suggestedMin: 0,
        position: index === 0 ? 'left' as const : 'right' as const,
        ticks: {
          callback: (value:any) => DatagridHelper.formatValue(column.type, value, column.precision, false, true)
        }
      };

      const datasetWithNegativeValues = datasets[index].data.find((value:any) => value < 0);
      if (!datasetWithNegativeValues) {
        options.scales[axisId as keyof DatagridColumn].min = 0;
      }

      index++;
    }
  });

  const data = {
    labels: labels.reverse(),
    datasets
  };

  return (
    <div className="chart-container" style={{ margin: "15px 0" }} >
      <ReactChart type="line" options={options} data={data} />
    </div>
  );
}